import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardList, Download, FileText, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import senerfiFormato from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const IS_SIG_MOBILE = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

const YES_NO_NA_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'SI', label: 'Si' },
  { value: 'NO', label: 'No' },
  { value: 'N/A', label: 'N/A' },
];

const YES_NO_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'SI', label: 'Si' },
  { value: 'NO', label: 'No' },
];

const GOOD_BAD_NA_OPTIONS = [
  { value: '', label: 'Seleccione...' },
  { value: 'B', label: 'Bueno' },
  { value: 'M', label: 'Malo' },
  { value: 'N/A', label: 'N/A' },
];

const WEEK_COLUMNS = [
  { key: 'lunes', label: 'Lunes', type: 'select', options: YES_NO_OPTIONS },
  { key: 'martes', label: 'Martes', type: 'select', options: YES_NO_OPTIONS },
  { key: 'miercoles', label: 'Miercoles', type: 'select', options: YES_NO_OPTIONS },
  { key: 'jueves', label: 'Jueves', type: 'select', options: YES_NO_OPTIONS },
  { key: 'viernes', label: 'Viernes', type: 'select', options: YES_NO_OPTIONS },
  { key: 'sabado', label: 'Sabado', type: 'select', options: YES_NO_OPTIONS },
  { key: 'domingo', label: 'Domingo', type: 'select', options: YES_NO_OPTIONS },
];

const ALTURA_WEEK_COLUMNS = [
  { key: 'lunes', label: 'L', type: 'select', options: GOOD_BAD_NA_OPTIONS },
  { key: 'martes', label: 'M', type: 'select', options: GOOD_BAD_NA_OPTIONS },
  { key: 'miercoles', label: 'Mi', type: 'select', options: GOOD_BAD_NA_OPTIONS },
  { key: 'jueves', label: 'J', type: 'select', options: GOOD_BAD_NA_OPTIONS },
  { key: 'viernes', label: 'V', type: 'select', options: GOOD_BAD_NA_OPTIONS },
  { key: 'sabado', label: 'S', type: 'select', options: GOOD_BAD_NA_OPTIONS },
  { key: 'domingo', label: 'D', type: 'select', options: GOOD_BAD_NA_OPTIONS },
];

function rowsFromStrings(items) {
  return items.map((label, index) => ({ id: `row_${index + 1}`, label }));
}

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function fmtDateDisplay(value) {
  if (!value) return '';
  const [y, m, d] = String(value).split('-');
  if (!y || !m || !d) return String(value);
  return `${d}/${m}/${y}`;
}

function parseJsonObject(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function assetToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function safeSlug(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

const DOCUMENTACION_META = {
  checklist_equipos_alturas: {
    title: 'Checklist equipos de alturas',
    description: 'Control documental y verificación previa de equipos para trabajo en alturas.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
    formCode: 'FT-SIG-069',
    version: '03',
    summaryColumns: [
      { key: 'contract_name', label: 'Contrato' },
      { key: 'assigned_to', label: 'Asignado a' },
      { key: 'brigade_name', label: 'Brigada' },
      { key: 'document_date', label: 'Fecha' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'document_date', label: 'Fecha', type: 'date', required: true },
          { key: 'contract_name', label: 'Contrato', required: true },
          { key: 'brigade_name', label: 'Brigada' },
          { key: 'assigned_to', label: 'Asignado a' },
          { key: 'assigned_role', label: 'Cargo' },
          { key: 'review_period', label: 'Periodo revisión' },
        ],
      },
    ],
    sections: [
      {
        key: 'inventory',
        type: 'fixed_rows',
        title: 'Inventario de elementos',
        columns: [
          { key: 'na', label: 'NA', type: 'select', options: YES_NO_OPTIONS },
          { key: 'brand', label: 'Marca', type: 'text' },
          { key: 'serial', label: 'Serie', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Eslinga de posicionamiento',
          'Tie off No 1',
          'Tie off No 2',
          'Tie off No 3',
          'Tie off No 4',
          'Mosqueton 1',
          'Mosqueton 2',
          'Mosqueton 3',
          'Mosqueton 4',
          'Arrestador, freno, Gibb',
          'Eslinga de proteccion contra caidas LM-LV-Grua',
          'Pretales',
          'Descendedor autofrenante ID',
          'Arnes LV, LM, Grua',
          'Poleas',
          'Ascenso y descenso R550',
          'Escalera',
        ]),
      },
      {
        key: 'weekly_condition',
        type: 'fixed_rows',
        title: 'Condición semanal de equipos',
        columns: ALTURA_WEEK_COLUMNS,
        rows: rowsFromStrings([
          'Eslinga de posicionamiento - cortes o rotura del tejido',
          'Eslinga de posicionamiento - desgaste, deformación o desgarro',
          'Eslinga de posicionamiento - estiramiento o elongación excesiva',
          'Tie off - fibras cortadas o desgastadas',
          'Tie off - corrosión',
          'Tie off - picaduras o grietas',
          'Mosquetones - desgaste excesivo o deformaciones',
          'Mosquetones - resortes con fallas',
          'Arrestador / freno / Gibb - defecto de funcionamiento',
          'Línea de vida - deterioro general',
          'Eslinga de protección contra caídas - humedad o presencia de hongos',
          'Pretales - banda de caucho',
          'Descendedor autofrenante ID - deterioro general',
          'Arnés - fibras cortadas o desgastadas',
          'Arnés - conserva su etiqueta',
          'Poleas - desgaste excesivo o deformaciones',
          'Ascenso y descenso R550 - desgaste excesivo o desgarro',
          'Escalera - largueros en buen estado',
          'Escalera - peldaños fijos y sin movimiento',
          'Escalera - zapatas de seguridad',
          'Escalera - trabapeldaños',
          'EPP - casco',
          'EPP - barbuquejo',
          'EPP - gafas claras u oscuras',
          'EPP - guantes de vaqueta o nitrilo',
          'Eslinga en Y - fibras cortadas o desgastadas',
          'Eslinga en Y - ganchos dieléctricos',
        ]),
      },
    ],
  },
  permiso_alturas: {
    title: 'Permiso alturas',
    description: 'Formato para autorización y control de actividades con trabajo seguro en alturas.',
    icon: CheckCircle2,
    gradient: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
    formCode: 'FT-SST-073',
    version: '2',
    summaryColumns: [
      { key: 'project_name', label: 'Proyecto' },
      { key: 'work_to_perform', label: 'Trabajo' },
      { key: 'approx_height', label: 'Altura aprox.' },
      { key: 'document_date', label: 'Fecha' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'document_date', label: 'Fecha', type: 'date', required: true },
          { key: 'project_name', label: 'Obra o proyecto', required: true },
          { key: 'start_time', label: 'Hora de inicio', type: 'time' },
          { key: 'end_time', label: 'Hora fin', type: 'time' },
          { key: 'routine_activity', label: 'Actividad rutinaria', type: 'select', options: YES_NO_OPTIONS },
          { key: 'work_to_perform', label: 'Trabajo a realizar' },
          { key: 'approx_height', label: 'Altura aprox.' },
          { key: 'preop_talk', label: 'Charla preoperacional' },
          { key: 'work_order_number', label: 'No. orden de trabajo' },
          { key: 'tools_to_use', label: 'Herramientas a utilizar', type: 'textarea' },
        ],
      },
    ],
    sections: [
      {
        key: 'workers',
        type: 'dynamic_rows',
        title: 'Validación documental del personal',
        columns: [
          { key: 'full_name', label: 'Nombres y apellidos', type: 'text' },
          { key: 'id_number', label: 'Cédula', type: 'text' },
          { key: 'role_name', label: 'Cargo', type: 'text' },
          { key: 'altura_certificate', label: 'Certificado de alturas vigente', type: 'select', options: YES_NO_OPTIONS },
          { key: 'altura_coordinator', label: 'Coordinador de alturas', type: 'select', options: YES_NO_OPTIONS },
          { key: 'arl_name', label: 'ARL', type: 'text', defaultValue: 'Positiva' },
          { key: 'eps_name', label: 'EPS', type: 'text' },
        ],
        initialRows: 3,
      },
      {
        key: 'health_conditions',
        type: 'fixed_rows',
        title: 'Condiciones de salud',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Se revisó la vigencia del concepto médico de aptitud ocupacional y/o certificado de alturas.',
          'El personal está en condiciones óptimas de salud para realizar el trabajo en alturas.',
          'No toma medicamentos que causen sueño o alteración de la conciencia.',
          'No ha consumido drogas alucinógenas en las últimas 24 horas.',
          'No sufre de epilepsia, mareos o vértigo.',
          'No presenta fobia a las alturas.',
          'No ha consumido bebidas alcohólicas en las últimas 24 horas.',
          'Ha recibido capacitación y entrenamiento en trabajos de alturas.',
        ]),
      },
      {
        key: 'epp_conditions',
        type: 'fixed_rows',
        title: 'EPP, EPCC y sistemas de acceso requeridos',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Casco con barbuquejo',
          'Botas de seguridad',
          'Lentes de seguridad',
          'Guantes de acuerdo a la actividad',
          'Uniforme',
          'Protección auditiva',
          'Protección respiratoria',
          'Arnés de cuerpo completo',
          'Eslinga de posicionamiento',
          'Eslinga doble o Y con amortiguador de impacto',
          'Eslinga doble o Y sin amortiguador de impacto',
          'Eslinga con amortiguador de impacto',
          'Pretales',
          'Tie off / anclaje portátil',
          'Línea de vida vertical portátil',
          'Mosquetones',
          'Adaptador de anclaje',
          'Sistema de rescate',
          'Auto stop',
          'Ascendedor',
          'Cuerda estática',
          'Escalera',
          'Grúa o canasta',
          'Conos - colombinas',
          'Cinta de señalización',
        ]),
      },
      {
        key: 'work_conditions',
        type: 'fixed_rows',
        title: 'Verificación de condiciones',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'El personal recibió instrucciones claras sobre riesgos y controles del ATS.',
          'El personal está libre de anillos, relojes, cadenas o accesorios de riesgo.',
          'El área de trabajo está demarcada y señalizada.',
          'Las condiciones climáticas son óptimas.',
          'Se dispone de procedimientos operativos y de seguridad específicos.',
          'El terreno para apoyo de escalera es firme y nivelado.',
          'Se dispone de medios de comunicación operativos.',
          'Las bases de postes o torres están en condiciones seguras.',
          'Las estructuras a intervenir están aptas para el ascenso.',
          'La actividad requiere canasta aérea, plataforma elevadora o grúa según evaluación.',
        ]),
      },
      {
        key: 'closure',
        type: 'fixed_rows',
        title: 'Cierre del permiso',
        columns: [{ key: 'value', label: 'Respuesta', type: 'text' }],
        rows: rowsFromStrings([
          'En el desarrollo de las labores hubo alguna novedad',
          'Cual fue la novedad',
          'El lugar quedó totalmente ordenado y limpio',
          'VoBo personal HSE',
          'Responsable del trabajo',
          'Hora del cierre',
        ]),
      },
    ],
  },
  permiso_riesgo_electrico: {
    title: 'Permiso Riesgo Electrico',
    description: 'Registro de autorización operativa y condiciones seguras para riesgo eléctrico.',
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #EA580C 0%, #EF4444 100%)',
    formCode: 'FT-SST-074',
    version: '1',
    summaryColumns: [
      { key: 'project_name', label: 'Proyecto' },
      { key: 'work_to_perform', label: 'Trabajo' },
      { key: 'voltage_level', label: 'Nivel tensión' },
      { key: 'document_date', label: 'Fecha' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'document_date', label: 'Fecha', type: 'date', required: true },
          { key: 'project_name', label: 'Obra o proyecto', required: true },
          { key: 'start_time', label: 'Hora de inicio', type: 'time' },
          { key: 'end_time', label: 'Hora fin', type: 'time' },
          { key: 'routine_activity', label: 'Actividad rutinaria', type: 'select', options: YES_NO_OPTIONS },
          { key: 'work_to_perform', label: 'Trabajo a realizar' },
          { key: 'voltage_level', label: 'Nivel de tensión' },
          { key: 'preop_talk', label: 'Charla preoperacional' },
          { key: 'work_order_number', label: 'No. orden de trabajo' },
          { key: 'tools_to_use', label: 'Herramientas a utilizar', type: 'textarea' },
        ],
      },
    ],
    sections: [
      {
        key: 'workers',
        type: 'dynamic_rows',
        title: 'Validación documental del personal',
        columns: [
          { key: 'full_name', label: 'Nombres y apellidos', type: 'text' },
          { key: 'id_number', label: 'Cédula', type: 'text' },
          { key: 'role_name', label: 'Cargo', type: 'text' },
          { key: 'arl_name', label: 'ARL', type: 'text', defaultValue: 'Positiva' },
          { key: 'eps_name', label: 'EPS', type: 'text' },
          { key: 'qualification_level', label: 'Nivel de habilitación', type: 'text' },
          { key: 'conte_category', label: 'Categoría CONTE', type: 'text' },
        ],
        initialRows: 3,
      },
      {
        key: 'health_conditions',
        type: 'fixed_rows',
        title: 'Condiciones de salud',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Se verificó la vigencia del concepto médico ocupacional y certificado de alturas.',
          'El trabajador está apto para realizar trabajos eléctricos.',
          'No usa medicamentos que alteren alerta o coordinación.',
          'No ha consumido sustancias psicoactivas o medicamentos sedantes en las últimas 24 horas.',
          'No presenta pérdida de conciencia, mareos o vértigo.',
          'No presenta alteraciones de reflejos o coordinación motriz.',
          'No ha consumido bebidas alcohólicas en las últimas 24 horas.',
          'No presenta molestias auditivas, visuales o condiciones que afecten estabilidad.',
        ]),
      },
      {
        key: 'methodology_conditions',
        type: 'fixed_rows',
        title: 'Condiciones de metodología de trabajos',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Se cumple con las cinco reglas de oro para trabajos con energía cero.',
          'Se respetan las distancias de seguridad RETIE.',
          'Se cuenta con personal habilitado para TCT y técnica definida.',
        ]),
      },
      {
        key: 'equipment',
        type: 'fixed_rows',
        title: 'EPP y EPC',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Casco con barbuquejo',
          'Botas de seguridad dieléctrica',
          'Careta ignífuga',
          'Guantes dieléctricos clase 00',
          'Guantes dieléctricos clase 0',
          'Guantes dieléctricos clase 2',
          'Guantes dieléctricos clase 4',
          'Mangas dieléctricas clase 2',
          'Mangas dieléctricas clase 4',
          'Detector de ausencia y/o pinza voltiamperimétrica con certificado vigente',
          'Cubridores, jumper, mantas y pinzas certificados',
          'Pértigas, bastones y varas mecánicas certificadas',
          'Andamio dieléctrico verificado',
          'Conjunto de traje conductor certificado',
          'Instrumentos de medición calibrados y vigentes',
          'Escalera dieléctrica certificada',
          'Grúa o canasta aislada certificada',
          'Conos - colombinas - malla',
          'Cinta de señalización roja',
        ]),
      },
      {
        key: 'verification',
        type: 'fixed_rows',
        title: 'Verificación',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'location', label: 'Ubicación / observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'El personal recibió instrucciones claras sobre riesgos y procedimientos.',
          'El personal está libre de accesorios metálicos o elementos personales de riesgo.',
          'Se cuenta con ATS o APA diligenciado y aprobado.',
          'Las condiciones climáticas son óptimas.',
          'Se cuenta con procedimientos operativos y de seguridad específicos.',
          'La actividad requiere presencia obligatoria de HSE o brigadista capacitado.',
          'Se cuenta con protocolos y medios de comunicación operativos.',
        ]),
      },
      {
        key: 'closure',
        type: 'fixed_rows',
        title: 'Cierre del permiso',
        columns: [{ key: 'value', label: 'Respuesta', type: 'text' }],
        rows: rowsFromStrings([
          'En el desarrollo de las labores hubo alguna novedad',
          'Cual fue la novedad',
          'El lugar quedó totalmente ordenado y limpio',
          'VoBo personal HSE',
          'Responsable del trabajo',
          'Hora del cierre',
        ]),
      },
    ],
  },
  ats: {
    title: 'ATS',
    description: 'Análisis de trabajo seguro con validación de riesgos, controles y responsables.',
    icon: FileText,
    gradient: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
    formCode: 'FT-SST-096',
    version: '001',
    summaryColumns: [
      { key: 'supervisor_name', label: 'Supervisor' },
      { key: 'city_name', label: 'Ciudad' },
      { key: 'project_name', label: 'Proyecto' },
      { key: 'document_date', label: 'Fecha' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'document_date', label: 'Fecha', type: 'date', required: true },
          { key: 'supervisor_name', label: 'Supervisor / responsable del trabajo', required: true },
          { key: 'city_name', label: 'Ciudad' },
          { key: 'project_name', label: 'Proyecto' },
          { key: 'activity_description', label: 'Descripción de la actividad', type: 'textarea' },
          { key: 'weather_rain', label: 'Lluvia', type: 'select', options: YES_NO_OPTIONS },
          { key: 'weather_wind', label: 'Viento fuerte', type: 'select', options: YES_NO_OPTIONS },
          { key: 'weather_storm', label: 'Tormenta eléctrica', type: 'select', options: YES_NO_OPTIONS },
          { key: 'weather_fog', label: 'Neblina', type: 'select', options: YES_NO_OPTIONS },
          { key: 'weather_sunny', label: 'Soleado', type: 'select', options: YES_NO_OPTIONS },
          { key: 'prepared_by_name', label: 'Nombre de quien diligencia el ATS' },
          { key: 'prepared_by_role', label: 'Cargo de quien diligencia' },
          { key: 'supervisor_sign_name', label: 'Nombre del supervisor o jefe de cuadrilla' },
          { key: 'supervisor_sign_role', label: 'Cargo supervisor' },
          { key: 'sst_name', label: 'Nombre responsable SST' },
          { key: 'sst_role', label: 'Cargo responsable SST' },
        ],
      },
    ],
    sections: [
      {
        key: 'work_points',
        type: 'dynamic_rows',
        title: 'Puntos de trabajo',
        columns: [
          { key: 'work_order', label: 'Orden de trabajo', type: 'text' },
          { key: 'physical_node', label: 'Nodo físico', type: 'text' },
          { key: 'reference_point', label: 'Punto de referencia', type: 'text' },
          { key: 'consigna', label: 'Consigna', type: 'text' },
          { key: 'ticket', label: 'Ticket', type: 'text' },
          { key: 'circuit', label: 'Circuito', type: 'text' },
          { key: 'act_number', label: 'Número de acta', type: 'text' },
          { key: 'zone_type', label: 'Zona', type: 'select', options: [{ value: '', label: 'Seleccione...' }, { value: 'Urbano', label: 'Urbano' }, { value: 'Rural', label: 'Rural' }] },
        ],
        initialRows: 2,
      },
      {
        key: 'attendance',
        type: 'dynamic_rows',
        title: 'Registro de asistencia',
        columns: [
          { key: 'full_name', label: 'Nombre y apellidos', type: 'text' },
          { key: 'id_number', label: 'Cédula', type: 'text' },
          { key: 'role_name', label: 'Cargo', type: 'text' },
          { key: 'employment_type', label: 'Directo / contratista', type: 'select', options: [{ value: '', label: 'Seleccione...' }, { value: 'Directo', label: 'Directo' }, { value: 'Contratista', label: 'Contratista' }] },
          { key: 'company_name', label: 'Empresa', type: 'text', defaultValue: 'SENERFI SAS' },
          { key: 'project_name', label: 'Obra / proyecto', type: 'text' },
          { key: 'novelty', label: 'Novedad', type: 'text' },
        ],
        initialRows: 3,
      },
      {
        key: 'tools',
        type: 'fixed_rows',
        title: 'Herramientas y equipos a utilizar',
        columns: [{ key: 'status', label: 'Usa', type: 'select', options: YES_NO_OPTIONS }],
        rows: rowsFromStrings([
          'Casco de seguridad',
          'Lentes de seguridad',
          'Botas dieléctricas',
          'Guantes dieléctricos',
          'Puntos de anclaje',
          'Conos reflectivos',
          'Señalización / letreros',
          'Kit de rescate',
          'Línea de vida',
          'Detector de ausencia de tensión',
          'Pinza voltiamperimétrica',
          'Pértiga telescópica',
          'Llave torx aislada',
          'Cinceles',
          'Sistema de puesta a tierra temporal',
          'Taladro',
          'Pulidora',
          'Grúa y sus componentes',
        ]),
      },
      {
        key: 'verification',
        type: 'fixed_rows',
        title: 'Lista de verificación',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'El personal identificó los peligros y administró los riesgos asociados.',
          'El personal comprende perfectamente el trabajo y las tareas.',
          'Se realizó inspección preoperacional a vehículos, herramientas y equipos.',
          'Las personas cuentan con las competencias para utilizar vehículos, equipos y herramientas.',
          'Se verificaron otros peligros como productos químicos, ventilación, animales o accesos.',
          'Se verificó que el trabajo no afecte a alguien alrededor.',
          'El área está demarcada o señalizada con cinta de precaución.',
          'Se conocen y entienden los procedimientos de trabajo seguro.',
          'Las condiciones de trabajo son óptimas.',
          'El personal conoce y aplica las 5 reglas de oro.',
          'El personal conoce, entiende y aplica la política STOP WORK.',
        ]),
      },
      {
        key: 'hazards',
        type: 'fixed_rows',
        title: 'Peligros / riesgos del entorno',
        columns: [
          { key: 'status', label: 'Aplica', type: 'select', options: YES_NO_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Atrapamiento durante manipulación de maquinaria, materiales y equipos.',
          'Golpes o cortes por manipulación inapropiada de herramientas.',
          'Golpes contra crucetas, aisladores o partes sobresalientes.',
          'Aplastamiento o cizallamiento.',
          'Superficies irregulares, desniveles o suelos resbaladizos.',
          'Obstáculos por maleza o cultivos con enredadera.',
          'Minas antipersona o condición pública insegura.',
          'Falta de señalización en áreas de trabajo.',
          'Condiciones deficientes de orden y aseo.',
          'Iluminación deficiente o instalaciones en mal estado.',
          'Accidentes de tránsito.',
          'Estrés laboral, fatiga o carga mental.',
          'Manipulación manual de cargas.',
          'Posturas prolongadas o forzadas.',
          'Ruido, temperatura extrema o vibración.',
          'Proximidad a redes o equipos energizados.',
          'Exposición a arco eléctrico por mala maniobra.',
          'Mal estado de estructuras de red.',
          'Electrocución por contacto directo o indirecto.',
          'Incendios o explosiones por arco eléctrico.',
          'Exposición a trabajo en alturas.',
          'Exposición química o material particulado.',
          'Exposición biológica.',
          'Riesgo natural por lluvia, tormenta o deslizamientos.',
          'Espacios confinados.',
          'Aspectos ambientales por derrames o residuos.',
        ]),
      },
      {
        key: 'analysis_rows',
        type: 'dynamic_rows',
        title: 'Identificación y análisis',
        columns: [
          { key: 'basic_step', label: 'Paso básico', type: 'text' },
          { key: 'risk_description', label: 'Peligro / riesgo / consecuencia', type: 'textarea' },
          { key: 'control_measure', label: 'Medidas preventivas y/o de control', type: 'textarea' },
        ],
        initialRows: 3,
      },
    ],
  },
  izaje: {
    title: 'Izaje',
    description: 'Formato de revisión documental y operativa para actividades de izaje.',
    icon: ClipboardList,
    gradient: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
    formCode: 'FT-SST-110',
    version: '01',
    summaryColumns: [
      { key: 'lift_date', label: 'Fecha' },
      { key: 'lift_place', label: 'Lugar' },
      { key: 'contract_name', label: 'Contrato' },
      { key: 'load_element', label: 'Elemento a izar' },
    ],
    fieldGroups: [
      {
        title: 'Datos generales',
        fields: [
          { key: 'document_date', label: 'Fecha del documento', type: 'date', defaultValue: todayYmd(), required: true },
          { key: 'lift_date', label: 'Fecha', type: 'date', required: true },
          { key: 'lift_place', label: 'Lugar' },
          { key: 'contract_name', label: 'Contrato' },
          { key: 'load_element', label: 'Elemento a izar' },
          { key: 'crane_reference', label: 'Referencia / marca equipo - grúa' },
          { key: 'energized_network', label: 'Red energizada', type: 'select', options: YES_NO_OPTIONS },
          { key: 'vehicle_plate', label: 'Placa' },
          { key: 'crane_type', label: 'Tipo grúa / equipo' },
          { key: 'gross_capacity', label: 'Capacidad bruta de la grúa (Kg/Ton)' },
          { key: 'max_operation_load', label: 'Carga máxima operación (Kg/Ton)' },
          { key: 'terrain_conditions', label: 'Condiciones del terreno', type: 'textarea' },
          { key: 'initial_radius', label: 'Radio inicial' },
          { key: 'final_radius', label: 'Radio final' },
          { key: 'initial_angle', label: 'Ángulo inicial' },
          { key: 'final_angle', label: 'Ángulo final' },
          { key: 'initial_boom_length', label: 'Longitud pluma inicial' },
          { key: 'final_boom_length', label: 'Longitud pluma final' },
          { key: 'initial_crane_capacity', label: 'Capacidad inicial grúa (Kg)' },
          { key: 'final_crane_capacity', label: 'Capacidad final grúa (Kg)' },
          { key: 'gross_load_calc', label: 'Cálculo de carga bruta', type: 'textarea' },
          { key: 'critical_lift', label: 'Se considera izaje crítico', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'wind_measurement', label: 'Medida del viento' },
        ],
      },
    ],
    sections: [
      {
        key: 'equipment_conditions',
        type: 'fixed_rows',
        title: 'Equipo',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: [{ value: '', label: 'Seleccione...' }, { value: 'B/M', label: 'B/M' }, { value: 'N/A', label: 'N/A' }] },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Swicht limite',
          'Banco válvulas',
          'Stop emergencia',
          'Gráficos de la carga (Display)',
          'Estado cable principal',
          'Anemómetro si aplica',
          'Alarmas',
          'Gancho / pateca principal',
          'Gancho / pateca auxiliar',
        ]),
      },
      {
        key: 'rigging_types',
        type: 'fixed_rows',
        title: 'Tipo de aparejos',
        columns: [{ key: 'value', label: 'Detalle', type: 'text' }],
        rows: rowsFromStrings([
          'Eslinga',
          'Estrobo',
          'Balancín',
          'Cadena',
          'Otro - cual',
          'Capacidad de eslingas',
          'Capacidad de grilletes',
          'Capacidad estrobos',
          'Balancín / orejas u otros puntos de anclaje o carga',
        ]),
      },
      {
        key: 'critical_conditions',
        type: 'fixed_rows',
        title: 'Se considera izaje crítico',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'El porcentaje de capacidad de la grúa es igual o superior al 80%',
          'Se requiere desplazamiento de la grúa con carga suspendida',
          'Maniobra conjunta con dos grúas o más',
          'Se considera izaje crítico',
          'Si la velocidad del viento es igual o superior a 40 km/h la maniobra no se desarrollará',
          'Se tiene identificado peso, volumen y punto de gravedad de la carga',
        ]),
      },
      {
        key: 'general_aspects',
        type: 'fixed_rows',
        title: 'Aspectos generales para el izaje',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Operador entrenado y certificado',
          'Se cuenta con los EPP adecuados',
          'El área de izaje y giro está delimitada y señalizada',
          'Se cuenta con sistema de comunicación entre operador y aparejador',
          'La grúa tiene la capacidad requerida',
          'Se cuenta con equipo de emergencias',
          'El apoyo de los gatos de la grúa es adecuado',
          'Ha sido realizado el permiso ATS para la tarea',
          'Se cuenta con cuerda guía para direccionamiento de la carga',
          'Ayudante / aparejador entrenado y competente',
        ]),
      },
      {
        key: 'preop_checks',
        type: 'fixed_rows',
        title: 'Lista de chequeo preoperacional de aparejos',
        columns: [
          { key: 'status', label: 'Estado', type: 'select', options: YES_NO_NA_OPTIONS },
          { key: 'observation', label: 'Observación', type: 'text' },
        ],
        rows: rowsFromStrings([
          'Estrobo / cable de acero - tres o más alambres rotos',
          'Estrobo / cable de acero - doblez o aplastamiento permanente',
          'Estrobo / cable de acero - corrosión',
          'Estrobo / cable de acero - ojo con guardacabo',
          'Cadena - deformación o elongación',
          'Cadena - desgaste',
          'Cadena - fisuras o hendiduras',
          'Cadena - corrosión',
          'Cabo marino - reducción del diámetro',
          'Cabo marino - cortes o muescas',
          'Cabo marino - áreas derretidas o carbonizadas',
          'Eslinga de nylon - quemadura por soldadura',
          'Eslinga de nylon - partes deshiladas o abrasión',
          'Eslinga de nylon - ausencia de etiqueta legible',
          'Grilletes - pasador de seguridad ausente',
          'Grilletes - oxidación',
          'Grilletes - contramarca ilegible',
          'Ganchos - pestillo de seguridad ausente',
          'Ganchos - garganta alargada',
          'Ganchos - fisuras o deformación',
          'Verificación equipo de transporte - capacidad de tarjeta de operación',
          'Verificación equipo de transporte - parales de seguridad',
          'Verificación equipo de transporte - amarre y soportes adecuados',
        ]),
      },
      {
        key: 'approvals',
        type: 'dynamic_rows',
        title: 'Firma autorización / aprobación',
        columns: [
          { key: 'position_name', label: 'Cargo', type: 'text' },
          { key: 'full_name', label: 'Nombre completo', type: 'text' },
          { key: 'id_number', label: 'Cédula', type: 'text' },
          { key: 'signature_note', label: 'Firma / observación', type: 'text' },
        ],
        initialRows: 4,
      },
    ],
  },
  aptitud_fisica: {
    title: 'Aptitud Fisica',
    description: 'Registro para validación física y aptitud del personal antes de la actividad.',
    icon: CheckCircle2,
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
    formCode: 'FT-SST-111',
    version: '00',
    summaryColumns: [
      { key: 'worker_name', label: 'Trabajador' },
      { key: 'worker_id', label: 'Cédula' },
      { key: 'work_to_perform', label: 'Trabajo' },
      { key: 'document_date', label: 'Fecha' },
    ],
    fieldGroups: [
      {
        title: 'Datos del trabajador',
        fields: [
          { key: 'document_date', label: 'Fecha', type: 'date', required: true },
          { key: 'worker_name', label: 'Nombre del trabajador', required: true },
          { key: 'worker_id', label: 'Cédula', required: true },
          { key: 'worker_role', label: 'Cargo' },
          { key: 'worker_area', label: 'Área' },
          { key: 'work_to_perform', label: 'Trabajo a realizar' },
          { key: 'project_name', label: 'Proyecto' },
          { key: 'assessment_period', label: 'Periodo de valoración' },
          { key: 'hseq_approval', label: 'Responsable HSEQ aprueba la actividad', type: 'textarea' },
        ],
      },
    ],
    sections: [
      {
        key: 'weekly_assessment',
        type: 'fixed_rows',
        title: 'Valoración semanal',
        columns: WEEK_COLUMNS,
        rows: rowsFromStrings([
          'Está tomando actualmente algún medicamento que le produzca sueño',
          'Ha ingerido alcohol en las últimas 24 horas',
          'Tiene alguna enfermedad, síntoma o limitación que impida el trabajo',
          'Sufre algún tipo de fobia (alturas / espacios cerrados)',
          'Durmió normalmente antes de iniciar su trabajo',
          'Ha comido en las últimas 4 horas',
          'Recibió entrenamiento en primeros auxilios en el último año',
          'Ha realizado trabajos similares anteriormente',
          'Sabe a quien dar aviso en caso de emergencia',
          'Ha tenido algún incidente de salud realizando trabajos similares',
          'Se realizó examen médico ocupacional de ingreso',
          'Se realizaron exámenes especializados para trabajos de alto riesgo',
          'Los resultados de exámenes permiten realizar actividades de alto riesgo',
          'La fecha de realización de exámenes es inferior a un año',
          'Le explicaron los procedimientos y se siente capaz de realizar estos trabajos',
          'Está certificado y con competencia para realizar trabajos de alto riesgo',
          'Está entrenado para usar EPP y EPCC en actividades de alto riesgo',
        ]),
      },
      {
        key: 'signatures',
        type: 'fixed_rows',
        title: 'Firmas y observaciones',
        columns: [{ key: 'value', label: 'Detalle', type: 'text' }],
        rows: rowsFromStrings([
          'Firma del trabajador',
          'Firma del responsable HSEQ / supervisor / jefe de brigada',
          'Observaciones',
        ]),
      },
    ],
  },
};

export const DOCUMENTACION_FORMULARIOS_OPTIONS = [
  { key: 'checklist_equipos_alturas', label: 'Checklist equipos de alturas', allowedRoles: ['Administrador', 'Administrativo', 'Operativo-Inspector'] },
  { key: 'permiso_alturas', label: 'Permiso alturas', allowedRoles: ['Administrador', 'Administrativo', 'Operativo'] },
  { key: 'permiso_riesgo_electrico', label: 'Permiso Riesgo Electrico', allowedRoles: ['Administrador', 'Administrativo', 'Operativo'] },
  { key: 'ats', label: 'ATS', allowedRoles: ['Administrador', 'Administrativo', 'Operativo'] },
  { key: 'izaje', label: 'Izaje', allowedRoles: ['Administrador', 'Administrativo', 'Operativo'] },
  { key: 'aptitud_fisica', label: 'Aptitud Fisica', allowedRoles: ['Administrador', 'Administrativo', 'Operativo'] },
];

function buildEmptySections(config) {
  const sections = {};
  (config.sections || []).forEach((section) => {
    if (section.type === 'fixed_rows') {
      const sectionValue = {};
      section.rows.forEach((row) => {
        sectionValue[row.id] = {};
        section.columns.forEach((col) => {
          sectionValue[row.id][col.key] = col.defaultValue || '';
        });
      });
      sections[section.key] = sectionValue;
      return;
    }
    if (section.type === 'dynamic_rows') {
      const rows = [];
      const count = section.initialRows || 1;
      for (let i = 0; i < count; i += 1) {
        const row = {};
        section.columns.forEach((col) => {
          row[col.key] = col.defaultValue || '';
        });
        rows.push(row);
      }
      sections[section.key] = rows;
    }
  });
  return sections;
}

function emptyForm(config) {
  const metadata = {};
  (config.fieldGroups || []).forEach((group) => {
    group.fields.forEach((field) => {
      metadata[field.key] = field.defaultValue || (field.type === 'date' ? todayYmd() : '');
    });
  });
  if (!metadata.document_date) metadata.document_date = todayYmd();
  return {
    id: '',
    metadata,
    sections: buildEmptySections(config),
    notes: '',
  };
}

function mergeSections(config, storedSections) {
  const base = buildEmptySections(config);
  const source = storedSections && typeof storedSections === 'object' ? storedSections : {};
  (config.sections || []).forEach((section) => {
    if (section.type === 'fixed_rows') {
      const sourceSection = source[section.key] && typeof source[section.key] === 'object' ? source[section.key] : {};
      section.rows.forEach((row) => {
        const sourceRow = sourceSection[row.id] && typeof sourceSection[row.id] === 'object' ? sourceSection[row.id] : {};
        section.columns.forEach((col) => {
          base[section.key][row.id][col.key] = sourceRow[col.key] || '';
        });
      });
      return;
    }
    if (section.type === 'dynamic_rows') {
      const sourceRows = Array.isArray(source[section.key]) ? source[section.key] : [];
      base[section.key] = sourceRows.length > 0 ? sourceRows.map((item) => {
        const row = {};
        section.columns.forEach((col) => {
          row[col.key] = item?.[col.key] || '';
        });
        return row;
      }) : base[section.key];
    }
  });
  return base;
}

export default function DocumentacionFormulariosModule({ location, documentationType = 'checklist_equipos_alturas' }) {
  const locationLabel = useMemo(() => {
    if (location === 'popayan') return 'Popayán';
    if (location === 'santander-de-quilichao') return 'Santander de Quilichao';
    return location || 'Sede';
  }, [location]);

  const config = useMemo(() => DOCUMENTACION_META[documentationType] || DOCUMENTACION_META.checklist_equipos_alturas, [documentationType]);
  const Icon = config.icon;
  const [form, setForm] = useState(() => emptyForm(config));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await api.sgi.documentation.list({ location, document_type: documentationType, limit: 1000, order: 'desc' });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.map((row) => ({
        ...row,
        metadata: parseJsonObject(row.metadata_json),
        sections: parseJsonObject(row.sections_json),
      })));
    } catch (err) {
      console.error('Error cargando documentación', err);
      setFeedback('No se pudieron cargar los registros de documentación.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(emptyForm(config));
    setFeedback('');
    loadRows();
  }, [config, location, documentationType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMetadataChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [key]: value,
      },
    }));
  };

  const onFixedCellChange = (sectionKey, rowId, colKey, value) => {
    setForm((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...(prev.sections[sectionKey] || {}),
          [rowId]: {
            ...((prev.sections[sectionKey] || {})[rowId] || {}),
            [colKey]: value,
          },
        },
      },
    }));
  };

  const onDynamicCellChange = (sectionKey, rowIndex, colKey, value) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[sectionKey]) ? [...prev.sections[sectionKey]] : [];
      currentRows[rowIndex] = {
        ...(currentRows[rowIndex] || {}),
        [colKey]: value,
      };
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionKey]: currentRows,
        },
      };
    });
  };

  const addDynamicRow = (section) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[section.key]) ? [...prev.sections[section.key]] : [];
      const newRow = {};
      section.columns.forEach((col) => {
        newRow[col.key] = col.defaultValue || '';
      });
      currentRows.push(newRow);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [section.key]: currentRows,
        },
      };
    });
  };

  const removeDynamicRow = (sectionKey, rowIndex) => {
    setForm((prev) => {
      const currentRows = Array.isArray(prev.sections[sectionKey]) ? [...prev.sections[sectionKey]] : [];
      currentRows.splice(rowIndex, 1);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionKey]: currentRows.length > 0 ? currentRows : [{}],
        },
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const requiredField = (config.fieldGroups || [])
      .flatMap((group) => group.fields)
      .find((field) => field.required && !String(form.metadata[field.key] || '').trim());

    if (requiredField) {
      setFeedback(`Completa el campo obligatorio: ${requiredField.label}.`);
      return;
    }

    try {
      setSaving(true);
      setFeedback('');
      await api.sgi.documentation.upsert({
        id: form.id || '',
        location,
        document_type: documentationType,
        document_date: form.metadata.document_date || '',
        metadata_json: form.metadata,
        sections_json: form.sections,
        notes: form.notes || '',
      });
      setForm(emptyForm(config));
      setFeedback(`Registro guardado correctamente en ${config.title}.`);
      await loadRows();
    } catch (err) {
      console.error('Error guardando documentación', err);
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    const metadata = { ...emptyForm(config).metadata, ...parseJsonObject(row.metadata_json || row.metadata) };
    const sections = mergeSections(config, parseJsonObject(row.sections_json || row.sections));
    setForm({
      id: row.id || '',
      metadata,
      sections,
      notes: row.notes || '',
    });
    setFeedback(`Editando registro #${row.id} de ${config.title}.`);
  };

  const onDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro #${row.id} de ${config.title}?`);
    if (!ok) return;
    try {
      await api.sgi.documentation.delete({ id: row.id });
      if (String(form.id) === String(row.id)) setForm(emptyForm(config));
      setFeedback('Registro eliminado.');
      await loadRows();
    } catch (err) {
      console.error('Error eliminando documentación', err);
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const exportExcel = async (selectedRows = rows) => {
    try {
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const headerTitle = config.title.toUpperCase();
      const sheetName = escapeHtml((config.title || 'Documentación').slice(0, 31));

      const formatFieldValue = (field, value) => {
        if (!value) return '';
        if (field?.type === 'date') return fmtDateDisplay(value);
        return String(value);
      };

      const buildMetaRows = (metadata) => {
        const fields = (config.fieldGroups || []).flatMap((group) => group.fields || []);
        const pairs = fields.map((field) => ({
          label: field.label,
          value: formatFieldValue(field, metadata?.[field.key] || ''),
        }));
        const rowsHtml = [];
        for (let i = 0; i < pairs.length; i += 2) {
          const left = pairs[i];
          const right = pairs[i + 1];
          rowsHtml.push(`
            <tr>
              <td class="meta-label">${escapeHtml(left?.label || '')}</td>
              <td class="meta-value">${escapeHtml(left?.value || '')}</td>
              <td class="meta-label">${escapeHtml(right?.label || '')}</td>
              <td class="meta-value">${escapeHtml(right?.value || '')}</td>
            </tr>
          `);
        }
        return rowsHtml.join('');
      };

      const normalizeSectionData = (section, sectionsData) => {
        const sectionData = sectionsData?.[section.key];
        if (section.type === 'fixed_rows') {
          return section.rows.map((row) => {
            const rowData = (sectionData && typeof sectionData === 'object' ? sectionData[row.id] : {}) || {};
            return { label: row.label, data: rowData };
          });
        }
        const dynamicRows = Array.isArray(sectionData) ? sectionData : [];
        const nonEmpty = dynamicRows.filter((row) => {
          if (!row || typeof row !== 'object') return false;
          return section.columns.some((col) => String(row[col.key] || '').trim() !== '');
        });
        return nonEmpty;
      };

      const buildSectionTable = (section, sections) => {
        if (section.type === 'fixed_rows') {
          const fixedRows = normalizeSectionData(section, sections);
          const body = fixedRows.map((r, idx) => `
            <tr>
              <td class="center">${escapeHtml(idx + 1)}</td>
              <td>${escapeHtml(r.label)}</td>
              ${section.columns.map((col) => `<td>${escapeHtml(r.data?.[col.key] || '')}</td>`).join('')}
            </tr>
          `).join('');
          return `
            <table>
              <tr><th class="section-cell" colspan="${2 + section.columns.length}">${escapeHtml(section.title.toUpperCase())}</th></tr>
              <tr>
                <th class="header w-no">#</th>
                <th class="header w-label">ITEM</th>
                ${section.columns.map((col) => `<th class="header">${escapeHtml(col.label)}</th>`).join('')}
              </tr>
              ${body}
            </table>
          `;
        }

        const dynamicRows = normalizeSectionData(section, sections);
        const body = dynamicRows.map((r, idx) => `
          <tr>
            <td class="center">${escapeHtml(idx + 1)}</td>
            ${section.columns.map((col) => `<td>${escapeHtml(r?.[col.key] || '')}</td>`).join('')}
          </tr>
        `).join('');
        return `
          <table>
            <tr><th class="section-cell" colspan="${1 + section.columns.length}">${escapeHtml(section.title.toUpperCase())}</th></tr>
            <tr>
              <th class="header w-no">#</th>
              ${section.columns.map((col) => `<th class="header">${escapeHtml(col.label)}</th>`).join('')}
            </tr>
            ${body || `<tr><td colspan="${1 + section.columns.length}" class="center">Sin registros</td></tr>`}
          </table>
        `;
      };

      const documentsHtml = (selectedRows || []).map((row) => {
        const metadata = parseJsonObject(row.metadata_json || row.metadata);
        const sections = parseJsonObject(row.sections_json || row.sections);
        const documentDate = metadata?.document_date || row.document_date || '';
        const metaTable = `
          <table>
            <tr>
              <td class="meta-label">SEDE</td>
              <td class="meta-value">${escapeHtml(locationLabel)}</td>
              <td class="meta-label">REGISTRO</td>
              <td class="meta-value">#${escapeHtml(row.id || '')}</td>
            </tr>
            ${buildMetaRows(metadata)}
          </table>
        `;
        const sectionsTables = (config.sections || []).map((section) => buildSectionTable(section, sections)).join('');
        return `
          <div class="page">
            <table class="header-table">
              <tr>
                <td class="logo-cell" colspan="1" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="4">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label">CODIGO:</td>
                <td class="meta-value">${escapeHtml(config.formCode)}</td>
              </tr>
              <tr>
                <td class="title" colspan="4">SISTEMA DE GESTION DE SEGURIDAD Y SALUD EN EL TRABAJO</td>
                <td class="meta-label">VERSION:</td>
                <td class="meta-value">${escapeHtml(config.version)}</td>
              </tr>
              <tr>
                <td class="title" colspan="4">${escapeHtml(headerTitle)}</td>
                <td class="meta-label">FECHA:</td>
                <td class="meta-value">${escapeHtml(fmtDateDisplay(documentDate || todayYmd()))}</td>
              </tr>
            </table>
            ${metaTable}
            ${sectionsTables}
            <table>
              <tr>
                <td class="meta-label">OBSERVACIONES GENERALES</td>
                <td class="meta-value" colspan="5">${escapeHtml(row.notes || '')}</td>
              </tr>
              <tr>
                <td class="meta-label">FIRMA RESPONSABLE</td>
                <td class="meta-value" colspan="2"></td>
                <td class="meta-label">FIRMA SST</td>
                <td class="meta-value" colspan="2"></td>
              </tr>
            </table>
          </div>
        `;
      }).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>${sheetName}</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; margin-bottom: 12px; }
              td, th { border: 1px solid #000; padding: 6px 8px; font-size: 10pt; vertical-align: middle; word-wrap: break-word; }
              .title { text-align: center; font-weight: 700; }
              .meta-label { font-weight: 700; text-align: left; width: 180px; }
              .meta-value { text-align: left; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .section-cell { background: #f3f4f6; font-weight: 700; text-align: left; }
              .logo-cell { text-align: center; }
              .logo-cell img { max-width: 180px; max-height: 70px; }
              .center { text-align: center; }
              .page { page-break-after: always; margin-bottom: 24px; }
              .w-no { width: 60px; }
              .w-label { width: 380px; }
              .header-table td { background: #fff; }
            </style>
          </head>
          <body>
            ${documentsHtml || '<table><tr><td>Sin registros para exportar</td></tr></table>'}
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `documentacion_${safeSlug(documentationType)}_${safeSlug(location || 'sede')}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Documentación y formularios SGI' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando documentación', err);
      setFeedback('No se pudo exportar el archivo Excel.');
    }
  };

  const renderField = (field) => {
    if (field.type === 'textarea') {
      return (
        <textarea
          className="w-full border rounded px-3 py-2 min-h-[90px]"
          value={form.metadata[field.key] || ''}
          onChange={(e) => onMetadataChange(field.key, e.target.value)}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          className="w-full border rounded px-3 py-2 bg-white"
          value={form.metadata[field.key] || ''}
          onChange={(e) => onMetadataChange(field.key, e.target.value)}
        >
          {(field.options || []).map((option) => (
            <option key={option.value || 'empty'} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field.type || 'text'}
        className="w-full border rounded px-3 py-2"
        value={form.metadata[field.key] || ''}
        onChange={(e) => onMetadataChange(field.key, e.target.value)}
      />
    );
  };

  const renderCell = (value, col, onChange) => {
    if (col.type === 'select') {
      return (
        <select className="w-full border rounded px-2 py-1 bg-white" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          {(col.options || []).map((option) => (
            <option key={option.value || 'empty'} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }
    if (col.type === 'textarea') {
      return (
        <textarea
          className="w-full border rounded px-2 py-1 min-h-[70px]"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    return (
      <input
        type={col.type || 'text'}
        className="w-full border rounded px-2 py-1"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm"
            style={{ background: config.gradient }}
          >
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <div className="text-base font-semibold text-gray-800">{config.title}</div>
            <div className="text-sm text-gray-600">{config.description}</div>
            <div className="text-xs text-gray-500 mt-1">Sede: {locationLabel} | Código: {config.formCode} | Versión: {config.version}</div>
          </div>
        </div>
        {!IS_SIG_MOBILE && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => exportExcel(rows)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110"
              style={{ background: config.gradient, borderColor: '#5b21b6' }}
            >
              <Download size={16} /> Exportar Excel
            </button>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-4 space-y-5">
        {(config.fieldGroups || []).map((group) => (
          <div key={group.title} className="space-y-3">
            <div className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">{group.title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {group.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">{field.label}{field.required ? ' *' : ''}</label>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </div>
        ))}

        {(config.sections || []).map((section) => (
          <div key={section.key} className="space-y-3">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 pb-2">
              <div className="text-sm font-semibold text-gray-800">{section.title}</div>
              {section.type === 'dynamic_rows' ? (
                <button
                  type="button"
                  onClick={() => addDynamicRow(section)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 text-xs"
                >
                  <Plus size={14} /> Agregar fila
                </button>
              ) : null}
            </div>
            <div className="overflow-x-auto border rounded-xl">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">{section.type === 'dynamic_rows' ? '#' : 'Ítem'}</th>
                    {section.type === 'dynamic_rows' ? null : <th className="px-3 py-2 text-left">Detalle</th>}
                    {section.columns.map((col) => (
                      <th key={col.key} className="px-3 py-2 text-left">{col.label}</th>
                    ))}
                    {section.type === 'dynamic_rows' ? <th className="px-3 py-2 text-left">Acción</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {section.type === 'fixed_rows' && section.rows.map((row, index) => (
                    <tr key={row.id} className="border-t align-top">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 min-w-[280px]">{row.label}</td>
                      {section.columns.map((col) => (
                        <td key={col.key} className="px-3 py-2 min-w-[140px]">
                          {renderCell(form.sections?.[section.key]?.[row.id]?.[col.key] || '', col, (value) => onFixedCellChange(section.key, row.id, col.key, value))}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {section.type === 'dynamic_rows' && (form.sections?.[section.key] || []).map((row, rowIndex) => (
                    <tr key={`${section.key}-${rowIndex}`} className="border-t align-top">
                      <td className="px-3 py-2 text-gray-500">{rowIndex + 1}</td>
                      {section.columns.map((col) => (
                        <td key={col.key} className="px-3 py-2 min-w-[160px]">
                          {renderCell(row?.[col.key] || '', col, (value) => onDynamicCellChange(section.key, rowIndex, col.key, value))}
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeDynamicRow(section.key, rowIndex)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} /> Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium mb-1">Observaciones generales</label>
          <textarea
            className="w-full border rounded px-3 py-2 min-h-[90px]"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </div>

        {feedback ? (
          <div className="text-sm rounded-lg border border-violet-200 bg-violet-50 text-violet-700 px-3 py-2">{feedback}</div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setForm(emptyForm(config));
              setFeedback('');
            }}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 border border-gray-300"
          >
            Limpiar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110 disabled:opacity-60"
            style={{ background: config.gradient, borderColor: '#5b21b6' }}
          >
            <Save size={16} /> {saving ? 'Guardando...' : 'Guardar documento'}
          </button>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div>
          <div className="text-sm font-semibold text-gray-800">Registros guardados</div>
          <div className="text-xs text-gray-500">{loading ? 'Cargando...' : `${rows.length} registros en ${config.title}`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">ID</th>
                {config.summaryColumns.map((col) => (
                  <th key={col.key} className="px-3 py-2 text-left">{col.label}</th>
                ))}
                <th className="px-3 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const metadata = parseJsonObject(row.metadata_json || row.metadata);
                return (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2">{row.id}</td>
                    {config.summaryColumns.map((col) => (
                      <td key={col.key} className="px-3 py-2">
                        {col.key === 'document_date' || col.key === 'lift_date'
                          ? fmtDateDisplay(metadata[col.key] || row.document_date || '')
                          : (metadata[col.key] || '-')}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {!IS_SIG_MOBILE && (
                          <button
                            type="button"
                            onClick={() => exportExcel([row])}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Download size={14} /> Exportar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                        >
                          <Pencil size={14} /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-gray-500" colSpan={config.summaryColumns.length + 2}>No hay registros guardados para este formato.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
